import React, { useState, useEffect } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '@/lib/auth';
import { api } from '@/lib/api';
import { NAV } from '@/constants/testIds';
import { Sparkles, LogOut, Calendar, ShieldCheck } from 'lucide-react';
import { StatusIndicator } from '@/components/StatusIndicator';
import { BookDemoModal } from '@/components/BookDemoModal';

export const Nav = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [demoOpen, setDemoOpen] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  // Lightweight admin check — the endpoint 403s for non-admins.
  useEffect(() => {
    let cancelled = false;
    if (!user) { setIsAdmin(false); return; }
    api.get('/admin/me')
      .then(() => { if (!cancelled) setIsAdmin(true); })
      .catch(() => { if (!cancelled) setIsAdmin(false); });
    return () => { cancelled = true; };
  }, [user]);

  const linkBase = 'px-3 py-2 rounded-md text-sm transition-colors';
  const linkClass = ({ isActive }) =>
    `${linkBase} ${isActive ? 'text-white' : 'text-white/70 hover:text-white hover:bg-white/5'}`;

  return (
    <>
      <BookDemoModal open={demoOpen} onClose={() => setDemoOpen(false)} source="nav_top" />
      <header data-testid={NAV.container} className="sticky top-0 z-40 w-full border-b border-border/60 bg-background/70 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-3">
          <Link to="/" data-testid={NAV.logo} className="flex items-center gap-2 group shrink-0">
            <motion.div
              whileHover={{ rotate: 12 }}
              className="h-9 w-9 rounded-xl grid place-items-center bg-gradient-to-br from-[#FF6B1A] to-[#FF8A4A] shadow-[0_0_0_1px_rgba(255,107,26,0.45),0_10px_28px_rgba(255,107,26,0.25)]"
            >
              <Sparkles className="h-5 w-5 text-white" />
            </motion.div>
            <div className="flex flex-col leading-tight">
              <span className="display text-base font-semibold tracking-tight">RevCore<span className="brand-green">.AI</span></span>
              <span className="text-[10px] uppercase tracking-[0.18em] text-white/40">Agentic Revenue OS</span>
            </div>
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            <NavLink to="/marketplace" data-testid={NAV.links.marketplace} className={linkClass}>Marketplace</NavLink>
            <NavLink to="/use-cases" className={linkClass}>Use cases</NavLink>
            <NavLink to="/builder" data-testid={NAV.links.builder} className={linkClass}>Builder</NavLink>
            <NavLink to="/docs" className={linkClass}>Docs</NavLink>
            <NavLink to="/pricing" data-testid={NAV.links.pricing} className={linkClass}>Pricing</NavLink>
            {user && <NavLink to="/dashboard" data-testid={NAV.links.dashboard} className={linkClass}>Dashboard</NavLink>}
            {isAdmin && (
              <NavLink
                to="/admin"
                data-testid="nav-link-admin"
                className={({ isActive }) => `${linkBase} inline-flex items-center gap-1.5 ${isActive ? 'text-[#39FF14]' : 'text-[#39FF14]/85 hover:text-[#39FF14] hover:bg-[#39FF14]/8'}`}
              >
                <ShieldCheck className="h-3.5 w-3.5" /> Admin
              </NavLink>
            )}
          </nav>

          <div className="hidden xl:flex items-center mr-2">
            <StatusIndicator />
          </div>

          <div className="flex items-center gap-2">
            {user ? (
              <>
                <div className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-full hairline text-xs">
                  <span className="h-2 w-2 rounded-full bg-[#39FF14] pulse-green" />
                  <span className="text-white/80">{user.name}</span>
                  <span className="text-white/40">/</span>
                  <span className="uppercase tracking-wide text-[10px] text-[#39FF14]">{user.plan}</span>
                </div>
                <button
                  onClick={async () => { await logout(); navigate('/'); }}
                  data-testid={NAV.logout}
                  className="btn-ghost flex items-center gap-1.5 rounded-md px-3 py-2 text-sm"
                >
                  <LogOut className="h-4 w-4" /> Logout
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => setDemoOpen(true)}
                  data-testid="nav-book-demo"
                  className="hidden sm:inline-flex btn-ghost items-center gap-1.5 rounded-md px-3 py-2 text-sm text-white/85 hover:text-white"
                >
                  <Calendar className="h-4 w-4 text-[#39FF14]" /> Book demo
                </button>
                <Link to="/login" data-testid={NAV.loginLink} className="btn-ghost rounded-md px-3 py-2 text-sm">Login</Link>
                <Link to="/marketplace" data-testid={NAV.primaryCta} className="btn-primary rounded-xl px-4 py-2 text-sm font-medium">
                  Deploy an Agent
                </Link>
              </>
            )}
          </div>
        </div>
      </header>
    </>
  );
};
